/**
 * <EditableEmployeeAvatar /> — wraps <EmployeeAvatar /> with a
 * hover overlay (camera icon) + click-to-upload + delete button.
 *
 * Used in the Employee Master File header so HR can update the
 * photo without ever leaving the page. The upload calls the new
 * POST /api/employees/{id}/photo endpoint (max 5 MB, JPG/PNG/WEBP/GIF).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, Trash2 } from "lucide-react";
import { useRef, useState } from "react";

import { api } from "../../lib/api";
import { EmployeeAvatar } from "./EmployeeAvatar";

export function EditableEmployeeAvatar({
  employeeId,
  photoUrl,
  firstName,
  lastName,
  size = 72,
}: {
  employeeId: string;
  photoUrl?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  size?: number;
}) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) =>
      api.uploadEmployeePhoto(employeeId, file),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["employee-master", employeeId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteEmployeePhoto(employeeId),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["employee-master", employeeId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const isBusy = upload.isPending || remove.isPending;

  // Bump the cache-bust whenever the photo changes so the browser
  // doesn't show the prior cached image after upload.
  // submittedAt is the timestamp of the last mutate call (exists
  // on react-query v5 UseMutationResult; dataUpdatedAt is not).
  const cacheBust =
    (upload.submittedAt ?? 0)
    || (remove.submittedAt ?? 0)
    || photoUrl
    || "";

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative group">
        <EmployeeAvatar
          employeeId={employeeId}
          photoUrl={photoUrl}
          firstName={firstName}
          lastName={lastName}
          size={size}
          cacheBust={cacheBust}
        />
        <button
          type="button"
          disabled={isBusy}
          onClick={() => fileInputRef.current?.click()}
          aria-label={photoUrl ? "Change photo" : "Upload photo"}
          className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center disabled:opacity-50"
        >
          <Camera
            size={Math.max(16, Math.floor(size * 0.3))}
            className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow"
            aria-hidden="true"
          />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
            e.target.value = "";  // allow re-upload of same filename
          }}
        />
      </div>
      {photoUrl && !isBusy && (
        <button
          type="button"
          onClick={() => remove.mutate()}
          className="text-xs text-text-tertiary hover:text-rose-700 inline-flex items-center gap-0.5"
        >
          <Trash2 size={11} /> Remove
        </button>
      )}
      {isBusy && (
        <span className="text-xs text-text-tertiary">
          {upload.isPending ? "Uploading…" : "Removing…"}
        </span>
      )}
      {error && (
        <p className="text-xs text-rose-700 max-w-[160px] text-center">
          {error}
        </p>
      )}
    </div>
  );
}
